/**
 * ICON STYLING: Do NOT add margin or size classes to icons inside Buttons.
 *
 * The Button handles icon spacing and sizing automatically:
 *   - default/lg: gap-2, size-4
 *   - sm: gap-1.5, size-3.5
 *   - xs: gap-1, size-3
 *
 * Bad:  <Button><Plus className="mr-2 h-4 w-4" />Add</Button>
 * Good: <Button><Plus />Add</Button>
 *
 * Only use size with ! (e.g., "size-5!") to intentionally override.
 */

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"

// Variants that get the "primary" depth effect (darker backgrounds)
const PRIMARY_DEPTH_VARIANTS = ["brand", "primary", "inverse", "destructive", "custom"]

// Tailwind classes for button depth effects
const DEPTH_PRIMARY =
  "shadow-[var(--shadow-btn-raised)] transition-shadow duration-100 ease-out active:shadow-none active:duration-75"
const DEPTH_LIGHT =
  "shadow-[var(--shadow-btn-raised-light)] transition-[box-shadow,border-color] duration-100 ease-out active:shadow-none active:duration-75"
const DEPTH_GHOST = "shadow-none transition-shadow duration-75 ease-out active:shadow-[var(--shadow-btn-inset)]"

// Disable vertical depress for popover/dropdown/combobox triggers (keep shadow animation)
// Matches: aria-haspopup (popovers, dropdowns) and role=combobox (selects) on self or parent
const POPUP_TRIGGER_OVERRIDES = [
  "[&[aria-haspopup]]:active:translate-y-0!",
  "[[aria-haspopup]_&]:active:translate-y-0!",
  "[&[role=combobox]]:active:translate-y-0!",
  "[[role=combobox]_&]:active:translate-y-0!",
].join(" ")

const buttonVariants = cva(
  // Base styles - same as Button but with faster transitions
  "group/Button relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-75 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Note: shadow classes removed - CSS module handles the raised shadow effect
        brand: "bg-brand text-black hover:bg-brand-hover font-medium!",
        primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
        inverse: "bg-primary-hover text-primary-foreground hover:bg-primary-active",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground [&_svg]:text-icon",
        secondary: "border border-tertiary bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        custom: "",
        outlineMuted:
          "border border-input bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground [&_svg]:text-icon-muted",
        outlineWarning:
          "border border-warning-border bg-warning-background text-warning-foreground hover:bg-warning-background/90 hover:text-warning-foreground [&_svg]:text-warning-foreground",
        outlineSuccess:
          "border border-green-border bg-green-tinted text-green-foreground hover:bg-green-background-hover hover:border-green-border-hover hover:text-green-foreground [&_svg]:text-green-foreground",
        outlineDestructive:
          "border border-red-border bg-red-tinted text-red-secondary-foreground hover:bg-red-background-hover hover:border-red-border-hover hover:text-red-secondary-foreground [&_svg]:text-red-secondary-foreground",
      },
      size: {
        default: "h-9 px-4 py-2",
        xs: "h-6 text-xs font-normal rounded-sm px-2 gap-1 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0",
        sm: "h-8 font-normal rounded-md px-2.5 gap-1.5 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
        lg: "h-10 rounded-md px-4 text-base",
        icon: "size-9",
        iconSm: "size-8",
        iconXs: "size-6",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "primary",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  asSpan?: boolean
  tooltip?: string
  tooltipSide?: "top" | "right" | "bottom" | "left"
  disableInteraction?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      asSpan = false,
      tooltip,
      tooltipSide = "top",
      disabled,
      disableInteraction = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : asSpan ? "span" : "button"

    // Determine which depth style to apply (skip if disableInteraction)
    const currentVariant = variant || "primary"
    let depthClass: string | undefined
    if (disableInteraction || currentVariant === "link") {
      depthClass = undefined
    } else if (currentVariant === "ghost") {
      depthClass = DEPTH_GHOST
    } else if (PRIMARY_DEPTH_VARIANTS.includes(currentVariant)) {
      depthClass = DEPTH_PRIMARY
    } else {
      depthClass = DEPTH_LIGHT
    }

    // Translate button down on active (skip for link variant and when disabled/disableInteraction)
    const translateClass =
      currentVariant !== "link" && !disabled && !disableInteraction ? "active:translate-y-px" : undefined

    const button = (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          depthClass,
          POPUP_TRIGGER_OVERRIDES,
          translateClass,
          className
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {children}
      </Comp>
    )

    if (tooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent sideOffset={4} side={tooltipSide}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )
    }

    return button
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
